// API contract for the employee profile tabs (Change Impacts / Tasks / Stakeholder Profiles).
// Single place to update when the backend contract changes: endpoints, params and
// response shapes (as received by reducers, i.e. after camelizeKeys where noted).

/**
 * @typedef {Object} OwnerInfo
 * @property {number} id
 * @property {string} name
 * @property {string} [avatar]
 * @property {boolean} archived
 */

/**
 * @typedef {Object} EmployeeChangeImpact
 * @property {number} id
 * @property {string} title
 * @property {?string} startDate
 * @property {?string} endDate
 * @property {?('Low'|'Medium'|'High')} scale
 * @property {?('Positive'|'Neutral'|'Negative')} perception
 * @property {?('Not Started'|'In Progress'|'Completed')} actionPlanStatus
 * @property {number} tasksNumber
 * @property {?{id: number, name: string, archived: boolean}} changeImpactType
 * @property {OwnerInfo[]} owners
 * @property {{id: number, name: string}} project
 * @property {?{id: number, name: string, canRead: boolean}} portfolio
 * @property {?{id: number, name: string}} parentPortfolio
 */

/**
 * @typedef {Object} EmployeeTask
 * @property {number} id
 * @property {string} title
 * @property {?string} description EditorJS JSON string
 * @property {?('Not Started'|'In Progress'|'Completed')} status
 * @property {?string} startDate
 * @property {?string} endDate
 * @property {OwnerInfo[]} owners
 * @property {number} changeImpactId
 * @property {{id: number, name: string, archived: boolean}} project
 */

/**
 * Paginated list (camelized).
 * @typedef {Object} PaginatedResponse
 * @property {number} total
 * @property {number} page
 * @property {number} perPage
 * @property {number} totalPages
 */

/**
 * GET employees/:id/stakeholders is requested with disabledCamelizeKeys, because
 * stakeholder rows have dynamic keys built from the project's custom field names
 * (same as GET projects/:id/stakeholders). The reducer camelizes `project` and
 * `stakeholder_fields` only, so StakeholdersTable gets the same shapes as on the
 * project stakeholders page.
 * @typedef {Object} EmployeeStakeholderProject
 * @property {number} id
 * @property {string} name
 * @property {?string} startDate
 * @property {?string} endDate
 * @property {?{id: number, name: string, archived: boolean}} projectStatus
 * @property {{id: number, firstName: string, lastName: string, email: string, fullName: string, archived: ?boolean, avatar: string}[]} owners
 *   same shape as project.owners on the project pages
 *
 * @typedef {Object} EmployeeStakeholderProjectGroup
 * @property {EmployeeStakeholderProject} project
 * @property {Object[]} stakeholderFields same shape as project.stakeholderFields
 * @property {Object[]} stakeholders raw keys (id, name, avatar, <field key>...)
 *
 * The "View" button and the name link open the stakeholder profile page
 * (STAKEHOLDERS_SHOW), which needs project.id and stakeholder id. Only return
 * stakeholders the viewer can read: project viewers have no access to
 * stakeholder analysis, so the link would otherwise lead to a permission error.
 */

export const EMPLOYEE_TABS_ENDPOINTS = {
  changeImpacts: (employeeId) => `employees/${employeeId}/change_impacts`,
  tasks: (employeeId) => `employees/${employeeId}/tasks`,
  stakeholders: (employeeId) => `employees/${employeeId}/stakeholders`,
};

// Response keys holding the list items (camelized)
export const EMPLOYEE_TABS_RESPONSE_KEYS = {
  changeImpacts: 'changeImpacts',
  tasks: 'tasks',
  stakeholderProjects: 'projects',
};

export const CHANGE_IMPACT_SORT_FIELDS = ['start_date', 'end_date'];
export const SORT_DIRECTIONS = ['asc', 'desc'];
